import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Copy, Check, Loader2, Lightbulb, Quote, Target, CalendarDays, FileDown, Sparkles, Layers, ExternalLink, Pencil, Trash2, Save, X, Compass, Info, RefreshCw, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import VoiceProfileCard from './VoiceProfileCard';
import ResearchCredibility from './ResearchCredibility';
import AutoCarouselButton from './AutoCarouselButton';
import GoalRegenerateChip from './GoalRegenerateChip';
import PostContextEditor from './PostContextEditor';
import CarouselDialog from './CarouselDialog';
import { linkedge } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { format as fmtDate } from 'date-fns';
import { renderInlineCitations } from '../../lib/renderInlineCitations';
import PaywallContentGate from './PaywallContentGate';

const engagementStyles = {
  High: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  Medium: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  Low: 'bg-muted text-muted-foreground border-border',
};

const CADENCES = [
  { value: 'daily', label: 'Daily (Mon-Fri)' },
  { value: '3x_week', label: '3x/week (Mon, Wed, Fri)' },
  { value: '2x_week', label: '2x/week (Tue, Thu)' },
  { value: 'weekly', label: 'Weekly (Tue)' },
  { value: 'bi_weekly', label: 'Bi-weekly (Tue, alt. weeks)' },
];

// ---------------- Auto-inferred audit plan context ----------------
// Sits between the Post Context editor and the researched posts grid so
// the user sees, in one glance, WHY these post drafts were chosen — the
// top LinkedIn presence themes and the first 30-day actions from their
// audit. Read-only surfacing: the actual data lives on the audit tabs.
function AuditPlanContextCard({ ctx }) {
  const themes = (ctx?.presencePlan || []).slice(0, 3);
  const today = (ctx?.thirtyDayPlan?.today || []).slice(0, 3);
  const week = (ctx?.thirtyDayPlan?.next7Days || []).slice(0, 3);
  if (!themes.length && !today.length && !week.length) return null;
  return (
    <Card
      className="border-primary/25 bg-gradient-to-br from-primary/[0.05] via-card to-[hsl(var(--accent))]/[0.04]"
      data-testid="audit-plan-context-card"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary shrink-0">
            <Compass size={16} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-foreground">Why these posts?</p>
              <Badge variant="secondary" className="text-[10px] font-mono-edge tracking-[0.14em]">AUTO-INFERRED FROM AUDIT</Badge>
            </div>
            <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed">
              Every draft below is aligned to your <span className="font-medium text-foreground">{ctx.selectedObjectiveLabel || 'career objective'}</span>
              {ctx.targetAudienceOrOpportunity ? <> for <span className="font-medium text-foreground">{ctx.targetAudienceOrOpportunity}</span></> : null}
              . We pulled themes from your LinkedIn Presence Plan and the actions your 30-Day Plan starts with — so posting these moves your positioning, not just your feed.
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="audit-plan-context-grid">
          {themes.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--accent))]/30 bg-[hsl(var(--accent))]/[0.06] p-3" data-testid="audit-plan-themes">
              <div className="flex items-center gap-1.5 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-[hsl(var(--accent))]">
                <Sparkles size={11} /> Presence themes
              </div>
              <ul className="mt-1.5 space-y-1 text-[12.5px]">
                {themes.map((t, i) => (
                  <li key={i} className="leading-snug">
                    <span className="font-medium text-foreground">{t.theme}</span>
                    {t.why && <span className="text-muted-foreground"> — {t.why}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {today.length > 0 && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.05] p-3" data-testid="audit-plan-today">
              <div className="flex items-center gap-1.5 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-emerald-700 dark:text-emerald-300">
                <Target size={11} /> Do today
              </div>
              <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground">
                {today.map((a, i) => (
                  <li key={i} className="leading-snug">• {a.action || a}</li>
                ))}
              </ul>
            </div>
          )}
          {week.length > 0 && (
            <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.05] p-3" data-testid="audit-plan-week">
              <div className="flex items-center gap-1.5 text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-sky-700 dark:text-sky-300">
                <CalendarDays size={11} /> This week
              </div>
              <ul className="mt-1.5 space-y-1 text-[12.5px] text-foreground">
                {week.map((a, i) => (
                  <li key={i} className="leading-snug">• {a.action || a}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="audit-plan-context-hint">
          <Info size={11} /> Full themes and 30-day plan live in the Audit Report tab.
        </p>
      </CardContent>
    </Card>
  );
}

export default function PostsResults({ posts, voice, regenerating, postsStatus, postsError, analysisId, existingCalendar, latestPov, povHistory, postContext: incomingContext, researchMeta, auditPlanContext, onRegenerateRequested, onRetryLast, contentGated = false, onUpgrade }) {
  const [selected, setSelected] = useState(new Set(posts.map((_, i) => i)));
  const [calendar, setCalendar] = useState(existingCalendar?.schedule || null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [downloadingFmt, setDownloadingFmt] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselPostIdx, setCarouselPostIdx] = useState(0);
  const [postsState, setPostsState] = useState(posts);
  const [postContext, setPostContext] = useState(incomingContext || null);
  const [voiceState, setVoiceState] = useState(voice);

  React.useEffect(() => {
    setVoiceState(voice);
  }, [voice]);

  React.useEffect(() => {
    setPostContext(incomingContext || null);
  }, [incomingContext]);

  // Keep postsState in sync if upstream posts change (e.g., after regenerate)
  React.useEffect(() => {
    setPostsState(posts);
    setSelected(new Set(posts.map((_, i) => i)));
  }, [posts]);

  const toggle = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(posts.map((_, i) => i)));
  const clearAll = () => setSelected(new Set());

  const onCalendarCreated = (schedule) => {
    setCalendar(schedule);
    setCalendarOpen(false);
    toast.success(`Calendar created with ${schedule.length} entries`);
  };

  const onDownload = async (fmt) => {
    setDownloadingFmt(fmt);
    try {
      await linkedge.downloadCalendar(analysisId, fmt);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloadingFmt(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top-of-page Generate CTA — always above the fold. The action
          bar further down also has a Generate button, but that one
          disappears behind the config cards + post list once the user
          scrolls. Users don't want to scroll to find it. */}
      {onRegenerateRequested && !contentGated && (
        <Card className="border-primary/25 bg-primary/[0.04]" data-testid="posts-top-cta">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="min-w-0">
              <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">RESEARCHED POSTS</p>
              <h2 className="font-display text-base sm:text-lg tracking-[-0.01em] mt-0.5">
                {postsState.length === 0
                  ? 'Draft 10 voice-matched posts from your audit + live research'
                  : `Regenerate the ${postsState.length} posts with fresh research`}
              </h2>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                Voice-matched, grounded in your audit + trending topics — carousels included where useful.
              </p>
            </div>
            <Button
              onClick={onRegenerateRequested}
              disabled={!!regenerating}
              className="shrink-0 rounded-full"
              size="lg"
              data-testid="posts-top-generate-cta"
            >
              {regenerating ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> Generating…</>
              ) : postsState.length === 0 ? (
                <><Sparkles size={14} className="mr-2" /> Generate posts</>
              ) : (
                <><Sparkles size={14} className="mr-2" /> Regenerate posts</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {analysisId && (
        <PostContextEditor
          analysisId={analysisId}
          context={postContext}
          onSaved={(ctx) => setPostContext(ctx)}
        />
      )}

      {/* Auto-inferred audit plan → tells the user WHY these researched
          posts landed on their board. Sources: full_audit_v2.presencePlan
          (top themes) + thirtyDayPlan (Today + Next 7 days). Read-only
          summary so people can align posts to their goal at a glance. */}
      {auditPlanContext && <AuditPlanContextCard ctx={auditPlanContext} />}

      {voiceState && (
        <VoiceProfileCard
          voice={voiceState}
          analysisId={analysisId}
          onSaved={(v) => setVoiceState(v)}
        />
      )}

      {/* Research credibility - grounded in actual analysis signals */}
      {researchMeta && postsState.length > 0 && (
        <ResearchCredibility
          postsCount={postsState.length}
          topicsCount={researchMeta.topicsCount || 0}
          leadersCount={researchMeta.leadersCount || 0}
          sourcesCount={researchMeta.sourcesCount || 0}
          role={researchMeta.role || ''}
          industry={researchMeta.industry || ''}
          country={researchMeta.country || ''}
          bundleFetchedAt={researchMeta.bundleFetchedAt || ''}
          canRefresh={!!researchMeta.canRefresh}
          refreshing={!!researchMeta.refreshing}
          postsStale={!!researchMeta.postsStale}
          onRefresh={researchMeta.onRefresh || null}
        />
      )}

      {/* Older analysis notice — when post count is below the current default */}
      {postsState.length > 0 && postsState.length < 10 && (
        <Card className="border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/8" data-testid="older-analysis-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <Sparkles size={18} className="text-[hsl(var(--warning))] mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold">This analysis is from an earlier version.</p>
              <p className="text-muted-foreground mt-0.5">
                Only {postsState.length} posts were generated and carousels weren&rsquo;t available yet. Use
                <span className="font-medium text-foreground"> &ldquo;Regenerate posts&rdquo;</span> below to
                get a full set of 10 voice-matched posts with 2-3 carousels included.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action bar */}
      <Card className="border-border" data-testid="posts-actionbar">
        <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="text-sm">
            <span className="font-mono-edge" data-testid="posts-selected-count">{selected.size}</span> of {postsState.length} posts selected
            <button onClick={selectAll} className="ml-3 text-primary hover:underline text-xs" data-testid="posts-select-all">Select all</button>
            <span className="mx-1 text-muted-foreground">·</span>
            <button onClick={clearAll} className="text-primary hover:underline text-xs" data-testid="posts-clear-all">Clear</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {onRegenerateRequested && !contentGated && (
              <Button
                variant="secondary"
                onClick={onRegenerateRequested}
                disabled={!!regenerating}
                className="rounded-xl"
                data-testid="posts-regenerate-cta"
              >
                {regenerating ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" /> Generating…</>
                ) : postsState.length === 0 ? (
                  <><Sparkles size={14} className="mr-2" /> Generate posts</>
                ) : (
                  <><Sparkles size={14} className="mr-2" /> Regenerate posts</>
                )}
              </Button>
            )}
            <Button
              onClick={() => setCalendarOpen(true)}
              disabled={selected.size === 0}
              className="rounded-xl"
              data-testid="open-calendar-builder"
            >
              <CalendarDays size={14} className="mr-2" /> Build content calendar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Empty-state CTA when there are literally zero posts and the user
          is on a plan that can generate them. Provides a big obvious
          entry point since the v2 layout no longer surfaces a
          "Generate posts" button in the page header. */}
      {postsState.length === 0 && !regenerating && onRegenerateRequested && !contentGated && (
        <Card className="border-primary/30 bg-primary/[0.04]" data-testid="posts-empty-cta">
          <CardContent className="p-6 flex flex-col items-center text-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Sparkles size={20} />
            </span>
            <div>
              <p className="text-base font-semibold">No researched posts yet</p>
              <p className="text-sm text-muted-foreground max-w-md mt-0.5">
                Blayzrr can draft 10 voice-matched posts (2-3 carousels included) based on your audit and the topics your ICP actually reads.
              </p>
            </div>
            <Button onClick={onRegenerateRequested} size="lg" className="rounded-full" data-testid="posts-empty-generate-cta">
              <Sparkles size={15} className="mr-2" /> Generate posts
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Existing calendar preview */}
      {calendar && calendar.length > 0 && (
        <CalendarPreview
          schedule={calendar}
          onDownload={onDownload}
          downloadingFmt={downloadingFmt}
        />
      )}

      {regenerating && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-primary" />
            <p className="text-sm">Writing fresh posts in your voice...</p>
          </CardContent>
        </Card>
      )}

      {/* Persistent error state with an in-place "Try again" button so the
          user doesn't have to scroll all the way back to the config card
          when a regenerate times out or fails. */}
      {!regenerating && postsStatus === 'error' && (
        <Card
          className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10"
          data-testid="posts-error-retry-card"
        >
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-start gap-2.5 min-w-0">
              <div className="mt-0.5 shrink-0 rounded-full bg-[hsl(var(--warning))]/25 p-1">
                <Sparkles size={14} className="text-[hsl(var(--warning))]" />
              </div>
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold">Post generation didn&rsquo;t finish</p>
                <p className="text-[12.5px] text-muted-foreground mt-0.5 break-words">
                  {postsError || 'The last regeneration ran into an error. You can retry with the same settings.'}
                </p>
              </div>
            </div>
            {(onRetryLast || onRegenerateRequested) && (
              <Button
                onClick={onRetryLast || onRegenerateRequested}
                className="shrink-0 rounded-full"
                size="sm"
                data-testid="posts-error-retry-btn"
              >
                <RefreshCw size={13} className="mr-1.5" /> Try again
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4" data-testid="posts-grid">
        {postsState.map((p, i) => {
          // Mark every 4th post as carousel-eligible so 2-3 of 10 are carousels
          const isCarousel = ((i + 1) % 4 === 0) || p.format_type === 'carousel' || (p.carousel && p.carousel.slides?.length);
          return (
            <PostCard
              key={i}
              post={p}
              index={i}
              checked={selected.has(i)}
              onToggle={() => toggle(i)}
              isCarousel={isCarousel}
              contentGated={contentGated}
              onUpgrade={onUpgrade}
              onOpenCarousel={() => {
                setCarouselPostIdx(i);
                setCarouselOpen(true);
              }}
              analysisId={analysisId}
              onPostUpdated={(updated) => {
                setPostsState((prev) => prev.map((x, idx) => (idx === i ? updated : x)));
              }}
              onPostReplaced={(idx, np) => {
                // GoalRegenerateChip flow: the backend returns a
                // prompt-post shape ({title, text, ...}) — merge onto the
                // existing researched-post row so the card keeps its
                // researched metadata (tone, engagement, citations…).
                setPostsState((prev) => prev.map((x, ix) => {
                  if (ix !== idx) return x;
                  return {
                    ...x,
                    text: np.text || np.post_text || x.text,
                    draft_text: np.text || np.post_text || x.draft_text,
                    hook: np.hook || x.hook,
                    title: np.title || x.title,
                    goal: np.goal || x.goal,
                    framework_regenerated: true,
                  };
                }));
              }}
              onPostDeleted={() => {
                setPostsState((prev) => prev.filter((_, idx) => idx !== i));
                setSelected((prev) => {
                  const next = new Set();
                  prev.forEach((idx) => {
                    if (idx < i) next.add(idx);
                    else if (idx > i) next.add(idx - 1);
                  });
                  return next;
                });
              }}
            />
          );
        })}
      </div>

      <CalendarBuilderDialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onCreated={onCalendarCreated}
        analysisId={analysisId}
        selectedIndices={Array.from(selected).sort((a, b) => a - b)}
        posts={postsState}
      />

      <CarouselDialog
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        analysisId={analysisId}
        postIdx={carouselPostIdx}
        post={postsState[carouselPostIdx]}
      />
    </div>
  );
}

function CalendarPreview({ schedule, onDownload, downloadingFmt }) {
  return (
    <Card className="border-primary/30 bg-gradient-to-br from-[hsl(var(--mint))]/30 via-card to-[hsl(var(--sky))]/20" data-testid="calendar-preview">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
              <CalendarDays size={16} />
            </span>
            <div>
              <p className="text-[10px] font-mono-edge text-primary">CONTENT CALENDAR</p>
              <h3 className="text-lg font-semibold mt-0.5" data-testid="calendar-count-headline">{schedule.length} posts scheduled</h3>
              <p className="text-sm text-muted-foreground">{fmtDate(new Date(schedule[0].date), 'MMM d')} → {fmtDate(new Date(schedule[schedule.length - 1].date), 'MMM d, yyyy')}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={() => onDownload('xlsx')} disabled={downloadingFmt === 'xlsx'} data-testid="calendar-download-xlsx">
              {downloadingFmt === 'xlsx' ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} className="mr-1.5" />} Excel
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onDownload('docx')} disabled={downloadingFmt === 'docx'} data-testid="calendar-download-docx">
              {downloadingFmt === 'docx' ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} className="mr-1.5" />} Word
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onDownload('pdf')} disabled={downloadingFmt === 'pdf'} data-testid="calendar-download-pdf">
              {downloadingFmt === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} className="mr-1.5" />} PDF
            </Button>
          </div>
        </div>
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="calendar-table">
            <thead className="bg-secondary text-xs font-mono-edge">
              <tr>
                <th className="text-left p-2.5">Date</th>
                <th className="text-left p-2.5">Day theme</th>
                <th className="text-left p-2.5">Post #</th>
                <th className="text-left p-2.5">Topic</th>
                <th className="text-left p-2.5">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((s, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-card' : 'bg-secondary/40'} data-testid="calendar-row">
                  <td className="p-2.5 whitespace-nowrap">
                    <span className="font-medium">{fmtDate(new Date(s.date), 'EEE, MMM d')}</span>
                  </td>
                  <td className="p-2.5">
                    <Badge className="bg-primary/10 text-primary border border-primary/20 font-mono-edge text-[10px]">{s.day_theme}</Badge>
                  </td>
                  <td className="p-2.5 font-mono-edge">#{s.post_number}</td>
                  <td className="p-2.5 truncate max-w-[260px]" title={s.topic_area}>{s.topic_area}</td>
                  <td className="p-2.5">
                    <Badge className={`font-mono-edge text-[10px] border ${engagementStyles[s.estimated_engagement] || engagementStyles.Medium}`}>
                      {s.estimated_engagement}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function CalendarBuilderDialog({ open, onClose, onCreated, analysisId, selectedIndices, posts }) {
  const today = new Date();
  const [cadence, setCadence] = useState('3x_week');
  const [startDate, setStartDate] = useState(today.toISOString().slice(0, 10));
  const [themed, setThemed] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const { calendar } = await linkedge.createCalendar(analysisId, {
        post_indices: selectedIndices,
        cadence,
        start_date: startDate,
        themed_days: themed,
      });
      onCreated(calendar);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not create calendar'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Build content calendar</DialogTitle>
          <DialogDescription>
            Scheduling {selectedIndices.length} post{selectedIndices.length === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cadence</Label>
            <Select value={cadence} onValueChange={setCadence}>
              <SelectTrigger data-testid="calendar-cadence">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CADENCES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Start date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="calendar-start-date" />
          </div>
          <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-border">
            <div>
              <Label className="cursor-pointer">Themed days</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Label each post day by its format (Opinion Day, Story Day, Framework Day, etc.).
              </p>
            </div>
            <Switch checked={themed} onCheckedChange={setThemed} data-testid="calendar-themed-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} disabled={submitting || selectedIndices.length === 0} className="rounded-xl" data-testid="calendar-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <CalendarDays size={14} className="mr-2" />} Build calendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PostThumbs — 👍 / 👎 buttons on each generated post. POSTs to
 * ``/api/posts/feedback``; server auto-promotes posts with 2+
 * consistent thumbs into the Post Writer Trainer corpus.
 */
function PostThumbs({ analysisId, post }) {
  const [state, setState] = React.useState(null); // 'up' | 'down' | null
  const [busy, setBusy] = React.useState(false);

  const send = async (rating) => {
    if (busy) return;
    const text = String(post?.polished || post?.text || post?.content || '').trim();
    if (!text) return;
    setBusy(true);
    const prev = state;
    setState(rating);
    try {
      const axios = (await import('axios')).default;
      const token = localStorage.getItem('le_token') || '';
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/posts/feedback`,
        {
          analysis_id: analysisId || '',
          post_id: String(post?.post_number || ''),
          post_text: text.slice(0, 4000),
          rating,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 12000 },
      );
    } catch (e) {
      setState(prev); // rollback UI
      // Silent — toast import would bloat this component; feedback is
      // fire-and-forget from the buyer's POV.
    } finally {
      setBusy(false);
    }
  };

  const btn = (rating, Icon, ariaLabel, activeCls) => {
    const isActive = state === rating;
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => send(rating)}
        disabled={busy}
        className={`px-2 ${isActive ? activeCls : ''}`}
        data-testid={`post-thumbs-${rating}-${post?.post_number}`}
        aria-label={ariaLabel}
        aria-pressed={isActive}
        title={ariaLabel}
      >
        <Icon size={14} />
      </Button>
    );
  };

  return (
    <div className="inline-flex items-center gap-0.5" data-testid={`post-thumbs-${post?.post_number}`}>
      {btn('up', ThumbsUp, 'This post is great', 'text-emerald-600 bg-emerald-500/10')}
      {btn('down', ThumbsDown, 'This post misses the mark', 'text-rose-600 bg-rose-500/10')}
    </div>
  );
}



function PostCard({ post, index, checked, onToggle, isCarousel, onOpenCarousel, analysisId, onPostUpdated, onPostDeleted, onPostReplaced, contentGated = false, onUpgrade }) {
  const [copied, setCopied] = useState(false);
  const [showStructure, setShowStructure] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [draft, setDraft] = useState({
    hook: post.hook || '',
    content: post.content || '',
    topic_area: post.topic_area || '',
    estimated_engagement: post.estimated_engagement || 'Medium',
  });

  // "Build carousel slides" (no cached carousel) now routes to the top-nav
  // Designer with the post text pre-filled. "View carousel + PDF" (cached)
  // keeps opening the in-place CarouselDialog so users don't lose their
  // saved slides.
  const openCarouselAction = () => {
    const hasCached = !!post.carousel?.slides?.length;
    if (hasCached) {
      onOpenCarousel?.();
      return;
    }
    const text = (
      post.content || post.linkedin_post || post.polished_text ||
      post.draft_text || post.text || ''
    ).trim();
    navigate('/carousel-designer', {
      state: {
        text,
        sourceMeta: {
          label: `From Post #${post.post_number ?? index + 1}`,
          analysisId,
          sourceType: 'post',
          sourceIdx: index,
        },
      },
    });
  };

  React.useEffect(() => {
    setDraft({
      hook: post.hook || '',
      content: post.content || '',
      topic_area: post.topic_area || '',
      estimated_engagement: post.estimated_engagement || 'Medium',
    });
  }, [post]);

  const onCopy = () => {
    if (contentGated) {
      toast.error('Copy is disabled on the Profile Success Plan. Upgrade to Thought Leadership to unlock.');
      return;
    }
    navigator.clipboard.writeText(post.content || '');
    setCopied(true);
    toast.success(`Post #${post.post_number} copied`);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSave = async () => {
    if (!analysisId) {
      toast.error('Missing analysis id');
      return;
    }
    const payload = {};
    if (draft.hook !== (post.hook || '')) payload.hook = draft.hook;
    if (draft.content !== (post.content || '')) payload.content = draft.content;
    if (draft.topic_area !== (post.topic_area || '')) payload.topic_area = draft.topic_area;
    if (draft.estimated_engagement !== (post.estimated_engagement || '')) {
      payload.estimated_engagement = draft.estimated_engagement;
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const res = await linkedge.updatePost(analysisId, index, payload);
      toast.success(`Post #${post.post_number} updated`);
      onPostUpdated?.(res.post);
      setEditing(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update post'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!analysisId) {
      toast.error('Missing analysis id');
      return;
    }
    setSaving(true);
    try {
      await linkedge.deletePost(analysisId, index);
      toast.success(`Post #${post.post_number} deleted`);
      onPostDeleted?.();
      setConfirmDelete(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete post'));
    } finally {
      setSaving(false);
    }
  };

  const engBadge = engagementStyles[post.estimated_engagement] || engagementStyles.Medium;
  const hasStructure = post.stance || (post.key_insights && post.key_insights.length > 0) || post.conclusion_line;
  const editedBy = post._last_edited_by;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>
      <Card
        className={`relative h-full flex flex-col overflow-hidden transition-all border-l-4 ${
          isCarousel
            ? 'border-[hsl(var(--peach))] bg-gradient-to-br from-[hsl(var(--peach))]/12 via-card to-[hsl(var(--sky))]/8 border-[hsl(var(--peach))]/40 hover:shadow-[0_18px_44px_-24px_rgba(0,0,0,0.35)]'
            : 'border-primary bg-gradient-to-br from-primary/[0.07] via-card to-[hsl(var(--accent))]/[0.06] border-primary/30 hover:shadow-[0_18px_44px_-24px_rgba(59,130,246,0.45)]'
        } ${checked ? 'ring-2 ring-primary/40' : ''}`}
        data-testid={`post-card-${post.post_number}`}
      >
        <CardContent className="p-5 flex flex-col h-full">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`post-select-${post.post_number}`} aria-label={`Select post ${post.post_number}`} />
              <Badge variant="secondary" className="font-mono-edge">#{post.post_number}</Badge>
              <Badge variant="secondary" className="font-mono-edge text-[11px]" data-testid={`post-format-${post.post_number}`}>
                {isCarousel ? 'carousel' : post.format_type}
              </Badge>
              {post.tone && (
                <Badge className="font-mono-edge text-[10px] bg-primary/10 text-primary border border-primary/20" data-testid={`post-tone-${post.post_number}`}>
                  {post.tone}
                </Badge>
              )}
              {post.polished_by && (
                <Badge
                  className="font-mono-edge text-[10px] bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30"
                  data-testid={`post-polished-${post.post_number}`}
                  title="Language polished for a conversational, low-jargon voice"
                >
                  <Sparkles size={10} className="mr-1" /> POLISHED
                </Badge>
              )}
              {isCarousel && (
                <Badge className="font-mono-edge text-[10px] border bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30">
                  <Layers size={10} className="mr-1" /> SWIPE
                </Badge>
              )}
              <Badge className={`font-mono-edge text-[11px] border ${engBadge}`} data-testid={`post-engagement-${post.post_number}`}>{post.estimated_engagement}</Badge>
            </div>
            <div className="flex items-center gap-1">
              {analysisId && !contentGated && (
                <GoalRegenerateChip
                  analysisId={analysisId}
                  source="researched"
                  index={(post.post_number || 1) - 1}
                  currentGoal={post.goal || null}
                  compact
                  onReplaced={(np) => {
                    onPostReplaced?.((post.post_number || 1) - 1, np);
                  }}
                />
              )}
              <Button variant="ghost" size="sm" onClick={onCopy} data-testid={`results-copy-post-button-${post.post_number}`}>
                {copied ? <Check size={14} className="mr-1.5 text-[hsl(var(--success))]" /> : <Copy size={14} className="mr-1.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <PostThumbs
                analysisId={analysisId}
                post={post}
                data-testid-prefix={`post-thumbs-${post.post_number}`}
              />
              {!editing && !contentGated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(true)}
                  data-testid={`results-edit-post-button-${post.post_number}`}
                  aria-label="Edit post"
                  title="Edit this post"
                >
                  <Pencil size={14} />
                </Button>
              )}
              {!editing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDelete(true)}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid={`results-delete-post-button-${post.post_number}`}
                  aria-label="Delete post"
                  title="Delete this post"
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3" data-testid={`post-topic-${post.post_number}`}>
            <span className="font-mono-edge">Topic:</span> {post.topic_area}
          </p>

          {hasStructure && (
            <button
              onClick={() => setShowStructure((s) => !s)}
              className="text-xs text-primary hover:underline mb-3 self-start"
              data-testid={`post-structure-toggle-${post.post_number}`}
            >
              {showStructure ? 'Hide breakdown ▴' : 'Show stance + insights ▾'}
            </button>
          )}

          {hasStructure && showStructure && (
            <div className="rounded-lg bg-secondary/60 p-3 mb-3 space-y-2.5 text-sm" data-testid={`post-structure-${post.post_number}`}>
              {post.stance && (
                <div className="flex gap-2">
                  <Target size={14} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-mono-edge text-[10px] text-muted-foreground">STANCE</p>
                    <p>{post.stance}</p>
                  </div>
                </div>
              )}
              {post.key_insights && post.key_insights.length > 0 && (
                <div className="flex gap-2">
                  <Lightbulb size={14} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-mono-edge text-[10px] text-muted-foreground">KEY INSIGHTS</p>
                    <ul className="space-y-1 mt-0.5">
                      {post.key_insights.map((k, i) => (<li key={i} className="flex gap-1.5"><span className="text-primary">→</span>{k}</li>))}
                    </ul>
                  </div>
                </div>
              )}
              {post.conclusion_line && (
                <div className="flex gap-2">
                  <Quote size={14} className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-mono-edge text-[10px] text-muted-foreground">CONCLUSION</p>
                    <p className="italic">&ldquo;{post.conclusion_line}&rdquo;</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {editing ? (
            <div className="space-y-3 flex-1" data-testid={`post-edit-form-${post.post_number}`}>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono-edge text-muted-foreground tracking-[0.18em]">
                  POST BODY
                </Label>
                <Textarea
                  value={draft.content}
                  onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  rows={12}
                  className="text-sm leading-relaxed font-normal whitespace-pre-wrap"
                  data-testid={`post-edit-content-${post.post_number}`}
                />
                <p className="text-[10px] text-muted-foreground font-mono-edge">
                  {draft.content.split(/\s+/).filter(Boolean).length} words
                  {(post.citations?.length || 0) > 0 && (
                    <span className="ml-2">
                      · Keep inline [^n] markers to preserve citation links
                    </span>
                  )}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono-edge text-muted-foreground tracking-[0.18em]">
                    HOOK (FIRST 1–2 LINES)
                  </Label>
                  <Input
                    value={draft.hook}
                    onChange={(e) => setDraft({ ...draft, hook: e.target.value })}
                    className="text-sm"
                    placeholder="The opening lines shown before 'see more'"
                    data-testid={`post-edit-hook-${post.post_number}`}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono-edge text-muted-foreground tracking-[0.18em]">
                    ENGAGEMENT
                  </Label>
                  <Select
                    value={draft.estimated_engagement}
                    onValueChange={(v) => setDraft({ ...draft, estimated_engagement: v })}
                  >
                    <SelectTrigger data-testid={`post-edit-engagement-${post.post_number}`}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono-edge text-muted-foreground tracking-[0.18em]">
                  TOPIC AREA
                </Label>
                <Input
                  value={draft.topic_area}
                  onChange={(e) => setDraft({ ...draft, topic_area: e.target.value })}
                  className="text-sm"
                  data-testid={`post-edit-topic-${post.post_number}`}
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                  data-testid={`post-edit-cancel-${post.post_number}`}
                >
                  <X size={14} className="mr-1" /> Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid={`post-edit-save-${post.post_number}`}
                >
                  {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <PaywallContentGate
              gated={contentGated}
              label="researched posts"
              testId={`post-gate-${post.post_number}`}
            >
              <p
                className="text-sm whitespace-pre-wrap flex-1 leading-relaxed"
                data-testid={`post-content-${post.post_number}`}
              >
                {(post.citations?.length || 0) > 0
                  ? renderInlineCitations(post.content, post.citations, {
                      testIdPrefix: `post-${post.post_number}-cite`,
                    })
                  : post.content}
              </p>
            </PaywallContentGate>
          )}

          {!editing && editedBy && (
            <p
              className="text-[10px] font-mono-edge text-muted-foreground mt-2"
              data-testid={`post-edit-meta-${post.post_number}`}
            >
              Edited by {editedBy.role === 'admin' ? 'admin' : 'owner'}
              {post._last_edited_at ? ` · ${fmtDate(new Date(post._last_edited_at), 'MMM d, h:mm a')}` : ''}
            </p>
          )}

          {(post.citations?.length || 0) > 0 && (
            <div
              className="mt-3 pt-3 border-t border-border/60"
              data-testid={`post-sources-${post.post_number}`}
            >
              <p className="font-mono-edge text-[10px] text-muted-foreground tracking-[0.18em] mb-2">
                SOURCES
              </p>
              <ul className="space-y-1.5">
                {post.citations.map((c) => (
                  <li key={c.n} className="text-[11px] leading-snug">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-start gap-1.5 text-foreground/85 hover:text-primary group"
                      data-testid={`post-${post.post_number}-source-${c.n}`}
                    >
                      <span className="font-mono-edge text-primary shrink-0 mt-[1px]">
                        [{c.n}]
                      </span>
                      <span className="line-clamp-1 group-hover:underline">
                        {c.title || c.url}
                      </span>
                      {c.publication && (
                        <span className="text-muted-foreground shrink-0">
                          · {c.publication}
                        </span>
                      )}
                      <ExternalLink size={10} className="text-muted-foreground shrink-0 mt-[3px]" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-[11px] text-muted-foreground font-mono-edge">
              {post.word_count || (post.content || '').split(/\s+/).filter(Boolean).length} words
              {(post.citations?.length || 0) > 0 && (
                <span className="ml-2">· {post.citations.length} citations</span>
              )}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={openCarouselAction}
              className="rounded-lg"
              data-testid={`open-carousel-${post.post_number}`}
              title={post.carousel?.slides?.length
                ? 'Recommended as a carousel'
                : 'Open the Carousel Designer with this post pre-filled'}
            >
              <Layers size={14} className="mr-1.5" />
              {post.carousel?.slides?.length ? 'View carousel + PDF' : 'Build carousel slides'}
            </Button>
            <AutoCarouselButton
              analysisId={analysisId}
              sourceType="post"
              sourceIdx={index}
              variant="outline"
              size="sm"
              cachedSlides={(post.auto_carousel_slides || 0)}
              postPreview={post?.content || post?.linkedin_post || post?.polished_text || post?.draft_text || post?.text || ''}
              className="rounded-lg ml-2"
            />
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete post #{post.post_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes this post from the analysis. The remaining posts will be
              renumbered. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving} data-testid={`post-delete-cancel-${post.post_number}`}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              disabled={saving}
              data-testid={`post-delete-confirm-${post.post_number}`}
            >
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
